import { t } from "../i18n";
import { $ } from "../lib/dom";
import { S } from "./state";

/**
 * Portrait phones: the side panel becomes a bottom sheet. Collapsed it fills
 * whatever space is left under the map (a "peek" at the latest log lines);
 * it opens on its own when a dialog starts and closes when the dialog ends.
 * The handle toggles it manually. Other layouts ignore all of this via CSS.
 */
const side = $("#side");
const handle = $<HTMLButtonElement>("#drawerHandle");
const stageWin = $("#stageWin");
const zoom = $("#zoom");

/** Minimum collapsed height: handle plus about one log line. */
const MIN_PEEK = 72;
/** Without a close-up, the open sheet leaves this fraction of the viewport for the map. */
const MAP_SHARE_WHEN_OPEN = 0.34;

let open = false;
let hadDialog = false;

function setOpen(v: boolean): void {
  open = v;
  side.classList.toggle("open", v);
  handle.setAttribute("aria-expanded", String(v));
  handle.lastElementChild?.replaceChildren(v ? t("side.hideLog") : t("side.showLog"));
  layout();
}

/** Recompute the two sheet heights from what's actually on screen. */
function layout(): void {
  const vh = innerHeight;
  const peek = Math.max(MIN_PEEK, vh - stageWin.getBoundingClientRect().bottom - 6);
  const top = zoom.classList.contains("show") ? zoom.getBoundingClientRect().bottom + 6 : vh * MAP_SHARE_WHEN_OPEN;
  side.style.setProperty("--peek", `${Math.round(peek)}px`);
  side.style.setProperty("--sheet", `${Math.round(Math.max(peek, vh - top))}px`);
}

/** Called whenever the side panel re-renders: follow the dialog's open/close. */
export function syncDrawer(): void {
  const hasDialog = S.dialog !== null;
  if (hasDialog && !hadDialog) setOpen(true);
  else if (!hasDialog && hadDialog) setOpen(false);
  hadDialog = hasDialog;
}

export function resetDrawer(): void {
  hadDialog = false;
  setOpen(false);
}

/** Lay out without animating (the game screen just appeared; heights measured while hidden are stale). */
export function snapDrawer(): void {
  side.style.transition = "none";
  layout();
  side.getBoundingClientRect();
  side.style.transition = "";
}

export function initDrawer(): void {
  // Tap toggles; a vertical swipe on the handle opens/closes in that direction.
  let startY: number | null = null;
  handle.addEventListener("pointerdown", (e) => {
    startY = e.clientY;
  });
  handle.addEventListener("pointerup", (e) => {
    if (startY === null) return;
    const dy = e.clientY - startY;
    startY = null;
    if (dy < -24) setOpen(true);
    else if (dy > 24) setOpen(false);
    else setOpen(!open);
  });
  const ro = new ResizeObserver(layout);
  ro.observe(stageWin);
  ro.observe(zoom);
  addEventListener("resize", layout);
  new MutationObserver(layout).observe(zoom, { attributes: true, attributeFilter: ["class"] });
  layout();
}
